import sendMessageToChatBot, { BASE_URL } from './SendMessageWithThread';
import sendPromptToGenerateImageChatbot from './SendPromptToGenerateImage';
import { DEFAULT_AI_GPT_MODEL } from './aiModels';

export interface IChatBotQuestionResult {
  text: string | undefined;
  durationMs: number;
}

export interface IChatBotQuestionStreamOptions {
  onTextDelta?: (text: string) => void;
  signal?: AbortSignal;
}

type ChatBotMessage = Array<{
  role: string;
  content: Array<{ type: string; text: string }>;
}>;

const handleQuestionFromChatBot = async (
  question: ChatBotMessage,
  enableSearch: boolean,
  model: string = DEFAULT_AI_GPT_MODEL
): Promise<IChatBotQuestionResult> => {
  const startedAt = performance.now();
  const response = await sendMessageToChatBot(question, enableSearch, model);
  const durationMs = Math.round(performance.now() - startedAt);

  if (!response) {
    return { text: undefined, durationMs };
  }

  const text = response.output
    .find((el: any) => el.role === 'assistant' && el.status === 'completed')
    ?.content?.at(0)?.text;

  return { text, durationMs };
};

const handleGenerateImageFromChatBot = async (
  question: ChatBotMessage,
  model: string = DEFAULT_AI_GPT_MODEL
): Promise<IChatBotQuestionResult> => {
  const startedAt = performance.now();
  const response = await sendPromptToGenerateImageChatbot(question, model);
  const durationMs = Math.round(performance.now() - startedAt);

  if (!response) {
    return { text: undefined, durationMs };
  }

  const imageBase64 = response.output?.find(
    (el) => el.type === 'image_generation_call' && el.status === 'completed'
  )?.result;

  const text = imageBase64 ? `data:image/png;base64,${imageBase64}` : undefined;

  return { text, durationMs };
};

const handleQuestionFromChatBotStreamMode = async (
  question: ChatBotMessage,
  enableSearch: boolean,
  model: string = DEFAULT_AI_GPT_MODEL,
  options: IChatBotQuestionStreamOptions = {}
): Promise<IChatBotQuestionResult> => {
  const startedAt = performance.now();
  let buffer = '';

  try {
    const response = await fetch(BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model,
        input: question,
        stream: true,
        text: {
          format: { type: 'text' },
          verbosity: 'medium',
        },
        tools: enableSearch
          ? [
              {
                type: 'web_search',
              },
            ]
          : [],
        store: true,
        include: [],
      }),
      signal: options.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      return {
        text: undefined,
        durationMs: Math.round(performance.now() - startedAt),
      };
    }

    const decoder = new TextDecoder();
    let remaining = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      const decodedChunkCandid = decoder.decode(value, { stream: true });
      const completeValueIndex = decodedChunkCandid.lastIndexOf('\n');
      const decodedChunk =
        remaining + decodedChunkCandid.slice(0, completeValueIndex);
      remaining = decodedChunkCandid.slice(completeValueIndex);

      const dataList = [...decodedChunk.matchAll(/data:\s?(\{.*\})/g)];

      for (const item of dataList) {
        try {
          const jsonData = JSON.parse(item[1]);

          if (jsonData?.type === 'response.output_text.delta') {
            buffer += jsonData.delta || '';
            options.onTextDelta?.(buffer);
          }
        } catch {
          // Ignore incomplete JSON chunks from the stream.
        }
      }
    }

    const durationMs = Math.round(performance.now() - startedAt);
    return { text: buffer || undefined, durationMs };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startedAt);

    if ((error as Error).name === 'AbortError') {
      return { text: buffer || undefined, durationMs };
    }

    console.error('Error in streaming chatbot response:', error);
    return { text: undefined, durationMs };
  }
};

export default handleQuestionFromChatBot;
export { handleQuestionFromChatBotStreamMode, handleGenerateImageFromChatBot };
